
import { z } from 'zod';

// Common validation schemas
export const emailSchema = z
  .string()
  .email('Please enter a valid email address')
  .min(1, 'Email is required');

export const phoneSchema = z
  .string()
  .regex(/^\+?[\d\s\-().]{7,20}$/, 'Please enter a valid phone number')
  .optional()
  .or(z.literal(''));

export const cgpaSchema = z
  .number()
  .min(0, 'CGPA must be between 0 and 10')
  .max(10, 'CGPA must be between 0 and 10')
  .optional();

export const urlSchema = z
  .string()
  .url('Please enter a valid URL')
  .optional()
  .or(z.literal(''));

export const requiredStringSchema = (fieldName: string, minLength: number = 1) =>
  z.string().min(minLength, `${fieldName} is required`);

export const optionalStringSchema = z.string().optional();

export const arrayOfStringsSchema = z.array(z.string()).default([]);

// Candidate validation schemas
export const candidateBasicInfoSchema = z.object({
  full_name: requiredStringSchema('Full name', 2),
  email: emailSchema,
  phone: phoneSchema,
});

export const candidateEducationSchema = z.object({
  college_id: optionalStringSchema,
  branch: optionalStringSchema,
  qualification: optionalStringSchema,
  cgpa: cgpaSchema,
  college_university: optionalStringSchema,
  latest_semester_score: z.number().optional(),
  enrollment_number: requiredStringSchema('Enrollment number'),
});

export const candidateLinksSchema = z.object({
  github_url: urlSchema,
  linkedin_url: urlSchema,
  social_links: arrayOfStringsSchema,
});

export const candidateSkillsSchema = z.object({
  key_skills: arrayOfStringsSchema,
  technologies: arrayOfStringsSchema,
  hobbies_interests: arrayOfStringsSchema,
});

export const candidateProjectsSchema = z.object({
  projects_details: arrayOfStringsSchema,
  project_links: arrayOfStringsSchema,
  certifications_awards: arrayOfStringsSchema,
  event_participations: arrayOfStringsSchema,
  internships_experience: arrayOfStringsSchema,
});

// Complete candidate schema
export const candidateSchema = candidateBasicInfoSchema
  .merge(candidateEducationSchema)
  .merge(candidateLinksSchema)
  .merge(candidateSkillsSchema)
  .merge(candidateProjectsSchema)
  .extend({
    permanent_address: optionalStringSchema,
    permanent_city: optionalStringSchema,
    current_address: optionalStringSchema,
    current_city: optionalStringSchema,
    position_applied: optionalStringSchema,
    status: z.string().default('applied'),
    current_round: optionalStringSchema,
    job_position_id: optionalStringSchema,
    resume_url: optionalStringSchema,
    resume_markdown: optionalStringSchema,
    parsed_resume_data: z.any().optional(),
  });

export type CandidateFormData = z.infer<typeof candidateSchema>;

// Question validation schemas
export const questionSchema = z.object({
  question_text: requiredStringSchema('Question text'),
  question_type: z.enum(['mcq', 'open_ended']),
  options: z.array(z.string()).optional(),
  correct_answer: optionalStringSchema,
  points: z.number().min(1, 'Points must be at least 1').default(1),
  difficulty_level: z.enum(['easy', 'medium', 'hard']).default('medium'),
  is_must_have: z.boolean().default(false),
  group_id: requiredStringSchema('Question group'),
});

export type QuestionFormData = z.infer<typeof questionSchema>;

// College validation schema
export const collegeSchema = z.object({
  name: requiredStringSchema('College name'),
  college_code: requiredStringSchema('College code'),
  location: optionalStringSchema,
  contact_person: optionalStringSchema,
  contact_email: emailSchema.optional().or(z.literal('')),
  contact_phone: phoneSchema,
  is_active: z.boolean().default(true),
  allowed_ip_ranges: arrayOfStringsSchema,
  registration_start_date: z.string().optional(),
  registration_end_date: z.string().optional(),
}).superRefine((data, ctx) => {
  if (
    data.registration_start_date &&
    data.registration_end_date &&
    data.registration_end_date < data.registration_start_date
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['registration_end_date'],
      message: 'Registration end date cannot be before start date',
    });
  }
});

export type CollegeFormData = z.infer<typeof collegeSchema>;

// Utility functions for validation
export const validateFormData = <T>(schema: z.ZodSchema<T>, data: unknown): T => {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const validationErrors = error.errors.map(err => ({
        field: err.path.join('.'),
        message: err.message,
        code: err.code,
      }));
      
      throw new Error(`Validation failed: ${validationErrors.map(e => e.message).join(', ')}`);
    }
    throw error;
  }
};

export const safeValidate = <T>(schema: z.ZodSchema<T>, data: unknown) => {
  try {
    return { success: true, data: schema.parse(data), error: null };
  } catch (error) {
    return { 
      success: false, 
      data: null, 
      error: error instanceof z.ZodError ? error : new Error('Validation failed') 
    };
  }
};
